import type { ColumnsType } from 'antd/es/table';
import { notification, Tooltip } from 'antd';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory, useRouteMatch } from 'react-router-dom';

import ActionButton from '@src/components/Buttons/ActionButton';
import { ResponsiveTable } from '@src/components/Table';
import type { IWorkJourneyManagementAttentionItem } from '@src/models/interfaces/workJourneyManagement.interface';
import { getFullUrl } from '@src/packages/utils';
import { ALLOWED_ROLES } from '@src/packages/utils/access';
import { DateUtil } from '@src/packages/utils/dates';
import type { WorkJourneyAssignedUser } from '@src/packages/workJourney/types';
import { useWorkJourneyValidationTranslations } from '@src/packages/workJourney/hooks/useWorkJourneyValidationTranslations';
import { notifyWorkJourneySaveError } from '@src/packages/workJourney/utils/submitWorkJourneyForm';
import WorkJourneyService from '@src/services/WorkJourneyService';

import { WORK_JOURNEY_ATTENTION_TABLE_COLUMN_WIDTH } from '../../../../constants';
import { useWorkJourneyManagementContext } from '../../../../context';
import { getAttentionDescription } from '../../../../utils/getAttentionDescription';
import UsersSelectionDrawer from '../../../../../WorkJourneyCreate/components/UsersSelectionDrawer';
import { getCoverPositionNavigation } from '../../../CoverPositionDrawers/getCoverPositionNavigation';
import {
  getActionLabelId,
  isPrimaryAction,
  WorkJourneyAttentionStatusBadge,
} from '../WorkJourneyAttentionStatusBadge';
import styles from './styles.module.less';

interface WorkJourneyAttentionTableProps {
  items: IWorkJourneyManagementAttentionItem[];
  loading?: boolean;
}

const workJourneyService = new WorkJourneyService();

export const WorkJourneyAttentionTable = ({
  items,
  loading = false,
}: WorkJourneyAttentionTableProps) => {
  const intl = useIntl();
  const history = useHistory();
  const { url } = useRouteMatch();
  const { orgId, refreshAttentionItems } = useWorkJourneyManagementContext();
  const validationTranslations = useWorkJourneyValidationTranslations();

  const [isUsersDrawerVisible, setIsUsersDrawerVisible] = useState(false);
  const [selectedWorkJourneyId, setSelectedWorkJourneyId] = useState<string | null>(null);
  const [selectedJourneyName, setSelectedJourneyName] = useState<string | null>(null);
  const [selectedRequiredMembers, setSelectedRequiredMembers] = useState(0);
  const [selectedDrawerUsers, setSelectedDrawerUsers] = useState<WorkJourneyAssignedUser[]>([]);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [savingMembers, setSavingMembers] = useState(false);

  const closeMembersDrawer = () => {
    setIsUsersDrawerVisible(false);
    setSelectedWorkJourneyId(null);
    setSelectedJourneyName(null);
    setSelectedRequiredMembers(0);
    setSelectedDrawerUsers([]);
  };

  const openMembersDrawer = async (record: IWorkJourneyManagementAttentionItem) => {
    const workJourneyConfigId = record.workJourneyConfigId;
    setSelectedWorkJourneyId(workJourneyConfigId);
    setSelectedJourneyName(record.workJourneyConfigName);
    setSelectedRequiredMembers(
      record.details?.requiredMembers ?? record.requiredMembers ?? 0,
    );
    setIsUsersDrawerVisible(true);
    setDrawerLoading(true);

    try {
      const members = (await workJourneyService.getMembers(
        workJourneyConfigId,
        orgId,
      )) as WorkJourneyAssignedUser[];

      setSelectedDrawerUsers(members);
    } catch (error) {
      notifyWorkJourneySaveError(error, intl, {
        messageId: 'workJourney.members.syncError',
        matchUrl: url,
      });
      closeMembersDrawer();
    } finally {
      setDrawerLoading(false);
    }
  };

  const handleMembersConfirm = async (selectedUsers: unknown[]) => {
    if (!selectedWorkJourneyId || !orgId) {
      closeMembersDrawer();
      return;
    }

    setSavingMembers(true);
    const members = selectedUsers as WorkJourneyAssignedUser[];
    try {
      await workJourneyService.updateMembers(
        selectedWorkJourneyId,
        orgId,
        members,
        validationTranslations,
      );
      notification.success({
        message: intl.formatMessage({ id: 'workJourney.members.syncSuccess' }),
      });
      closeMembersDrawer();
      await refreshAttentionItems();
    } catch (error) {
      notifyWorkJourneySaveError(error, intl, {
        messageId: 'workJourney.members.syncError',
        members,
        matchUrl: url,
      });
    } finally {
      setSavingMembers(false);
    }
  };

  const handleAttentionAction = (record: IWorkJourneyManagementAttentionItem) => {
    // Staffing shortage: assign missing members (not a real hiring flow for now).
    if (record.actionType === 'hiring') {
      void openMembersDrawer(record);
      return;
    }

    if (record.actionType !== 'cover_position' && record.actionType !== 'resolve') {
      return;
    }

    const { path, state } = getCoverPositionNavigation(record);
    history.push(getFullUrl(path, url), state);
  };

  const formatAttentionDate = (value: string | null) => {
    if (value) return DateUtil.toDateString(value, DateUtil.DEFAULT_DT_DATE_FORMAT);
    return intl.formatMessage({ id: 'workJourney.management.columns.date.ongoing' });
  };

  const columns: ColumnsType<IWorkJourneyManagementAttentionItem> = [
    {
      title: intl.formatMessage({ id: 'workJourney.management.columns.dateFrom' }),
      key: 'dateFrom',
      width: WORK_JOURNEY_ATTENTION_TABLE_COLUMN_WIDTH.DATE,
      render: (record: IWorkJourneyManagementAttentionItem) =>
        formatAttentionDate(record.dateFrom ?? record.date),
    },
    {
      title: intl.formatMessage({ id: 'workJourney.management.columns.dateTo' }),
      key: 'dateTo',
      width: WORK_JOURNEY_ATTENTION_TABLE_COLUMN_WIDTH.DATE,
      render: (record: IWorkJourneyManagementAttentionItem) =>
        formatAttentionDate(record.dateTo ?? record.dateFrom ?? record.date),
    },
    {
      title: intl.formatMessage({ id: 'workJourney.management.columns.journey' }),
      key: 'journey',
      width: WORK_JOURNEY_ATTENTION_TABLE_COLUMN_WIDTH.JOURNEY,
      render: (record: IWorkJourneyManagementAttentionItem) => (
        <Tooltip title={record.workJourneyConfigName}>
          <span className={styles.journeyCell} title={record.workJourneyConfigName}>
            {record.workJourneyConfigName}
          </span>
        </Tooltip>
      ),
    },
    {
      title: intl.formatMessage({ id: 'workJourney.management.columns.reason' }),
      key: 'reason',
      width: WORK_JOURNEY_ATTENTION_TABLE_COLUMN_WIDTH.REASON,
      render: (record: IWorkJourneyManagementAttentionItem) => (
        <WorkJourneyAttentionStatusBadge reason={record.reason} />
      ),
    },
    {
      title: intl.formatMessage({ id: 'workJourney.management.columns.description' }),
      key: 'description',
      render: (record: IWorkJourneyManagementAttentionItem) =>
        getAttentionDescription(record, intl.formatMessage),
    },
    {
      title: '',
      key: 'action',
      align: 'right',
      render: (record: IWorkJourneyManagementAttentionItem) => (
        <ActionButton.Container
          buttons={[
            <ActionButton
              key={`${record.id}-action`}
              label={intl.formatMessage({ id: getActionLabelId(record.actionType) })}
              onClick={() => handleAttentionAction(record)}
              type={isPrimaryAction(record.actionType) ? 'primary' : 'default'}
              color={isPrimaryAction(record.actionType) ? 'primary' : 'secondary'}
              size="medium"
              allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.UPDATE}
              className={styles.attentionActionButton}
              disabledScaling
            />,
          ]}
        />
      ),
    },
  ];

  return (
    <>
      <ResponsiveTable dataSource={items} columns={columns} loading={loading} />

      <UsersSelectionDrawer
        isVisible={isUsersDrawerVisible}
        onCancel={closeMembersDrawer}
        onConfirm={users => void handleMembersConfirm(users)}
        selectedUsers={selectedDrawerUsers}
        loading={drawerLoading || savingMembers}
        title={intl.formatMessage({ id: 'general.users.assigned' })}
        description={selectedJourneyName ?? undefined}
        requiredMembers={selectedRequiredMembers}
      />
    </>
  );
};
